import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {useProduct} from '@amblique/king-living/app/hooks/use-product';

import {
  Box,
  Flex,
  Image,
  Skeleton,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {PIM_SQUARE_THUMBNAIL_PX, resolveProductThumbnailUrl} from '@amblique/king-living/app/components/configuration-selector/utils';
import {GET_PRODUCTS_PARAMS, GET_PRODUCTS_STALE_TIME_MS} from '@amblique/king-living/app/utils/api-utils';

/**
 * Cover row for a Finished Good (TSD §7).
 *
 * Resolves the variation product referenced by `coverVariantId` and renders it as a
 * single static row inside the "What's included" list. Per the design, the cover row
 * shares the row chrome (border, padding, image size) with the bundled option rows
 * below, but **inverts the name-line order** versus the option rows so the cover
 * material reads as the primary information and the item type sits as a small caption:
 *
 *   - Top line  (bold, `primaryName` style) — cover variant product name,
 *     then `c_materialName` as legacy fallback.
 *   - Bottom line (gray, `secondaryLabel` style) — the variant's `c_itemType` (e.g.
 *     "Fabric", "Leather"). Hidden if absent.
 *
 * Why we don't reuse `<RadioOptionContent>`: that component is locked to the option
 * pattern (shortDescription on top, product name below), which is the inverse of what
 * the cover row needs.
 */
export function FinishedGoodCoverRow({coverVariantId}) {
  const styles = useMultiStyleConfig('FinishedGoodView', {});

  const {data: coverVariant, isLoading} = useProduct(
    {
      parameters: {
        ...GET_PRODUCTS_PARAMS,
        id: coverVariantId,
      },
    },
    {enabled: Boolean(coverVariantId), staleTime: GET_PRODUCTS_STALE_TIME_MS}
  );

  if (!coverVariantId) return null;

  return (
    <Box className="finished-good-cover-row" {...styles.section}>
      <Text as="p" className="finished-good-cover-row__heading" {...styles.sectionHeading}>
        <FormattedMessage defaultMessage="Cover" id="customize_panel.cover.label" />
      </Text>
      {isLoading || !coverVariant ? (
        <Skeleton height="126px" />
      ) : (
        <Box className="finished-good-cover-row__item" {...styles.coverItem}>
          <Flex className="finished-good-cover-row__row" {...styles.itemRow}>
            {(() => {
              const imageUrl = resolveProductThumbnailUrl(coverVariant);

              return imageUrl ? (
                <Image
                  alt={coverVariant.name}
                  className="finished-good-cover-row__image"
                  htmlHeight={PIM_SQUARE_THUMBNAIL_PX}
                  htmlWidth={PIM_SQUARE_THUMBNAIL_PX}
                  loading="lazy"
                  src={imageUrl}
                  {...styles.coverItemImage}
                />
              ) : null;
            })()}
            <Flex className="finished-good-cover-row__text" {...styles.itemText}>
              <Text
                className="finished-good-cover-row__material"
                {...styles.primaryName}
              >
                {(coverVariant.name || '').trim() || coverVariant.c_materialName || ''}
              </Text>
              {coverVariant.c_itemType && (
                <Text
                  className="finished-good-cover-row__item-type"
                  {...styles.secondaryLabel}
                >
                  {coverVariant.c_itemType}
                </Text>
              )}
            </Flex>
          </Flex>
        </Box>
      )}
    </Box>
  );
}

FinishedGoodCoverRow.displayName = 'FinishedGoodCoverRow';

FinishedGoodCoverRow.propTypes = {
  coverVariantId: PropTypes.string,
};
